/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type * as SelectPrimitive from "@radix-ui/react-select";
import type { ExoticComponent, JSX, RefAttributes } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import { Select, SelectValue } from "../Select";
import { SelectContent } from "../SelectContent.emotion";
import { SelectItem } from "../SelectItem.emotion";
import { SelectTrigger } from "../SelectTrigger.emotion";

export type SelectMenuProps = SelectPrimitive.SelectProps & {
  options: { label: string; value: string }[];
  placeholder?: string;
  triggerCSS?: Interpolation;
  contentCSS?: Interpolation;
  contentProps?: SelectPrimitive.SelectContentProps;
};

export const SelectMenu = (({
  options,
  placeholder,
  triggerCSS = {},
  contentCSS = {},
  contentProps = {},
  ...props
}: SelectMenuProps) => {
  const t = useTranslations();
  return (
    <Select {...props}>
      <SelectTrigger css={triggerCSS}>
        <SelectValue placeholder={placeholder ?? t("general_word_select")} />
      </SelectTrigger>
      <SelectContent {...contentProps} css={contentCSS}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}) as ExoticComponent<SelectMenuProps & RefAttributes<HTMLSelectElement>> & {
  Field: (props: SelectMenuFieldProps) => JSX.Element;
};

type SelectMenuFieldProps = SelectMenuProps & {
  control: Control<any, any>;
};

SelectMenu.Field = ({
  control,
  name,
  required,
  ...props
}: SelectMenuFieldProps) => {
  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <SelectMenu
      {...field}
      {...props}
      value={field.value}
      ref={ref}
      onValueChange={(value) => field.onChange?.(value)}
    />
  );
};
